import { UI } from '@skedulo/protobuf-web'
import { compareByRelevance } from '../../utils'
import { UiApiCrudClientBaseV1 } from '../crud-client-base'
import type {
  IValidateDataRequest,
  IValidateDataResponse,
  IValidation,
  IValidationCreateRequest,
  IValidationDeleteRequest,
  IValidationGetManyRequest,
  IValidationGetRequest,
  IValidationPutRequest,
} from '../../../types'

const BASE_PATH = '/v1/validations'

export class ValidationUiApiClientV1 extends UiApiCrudClientBaseV1<IValidation> {
  get = async (request: IValidationGetRequest) =>
    this.restGet<IValidation>(`${BASE_PATH}/${request.uid}`)

  getMany = async (request: IValidationGetManyRequest) => {
    const { limit, page, name, uncomposed, ...rest } = request as Record<
      string,
      unknown
    >
    const queryParams = this.buildQueryParams({
      limit,
      page,
      name,
      uncomposed,
      ...rest,
    })
    const response = await this.restGet<{ items: IValidation[] }>(
      BASE_PATH,
      queryParams,
    )
    if (!response.items) throw new Error('Invalid response: missing "items"')
    return response.items.map((item) => this.getItem(item))
  }

  getBestValidation = async (name: string): Promise<IValidation | null> =>
    (await this.getMany({ name, limit: 1000 })).sort(compareByRelevance)[0] ||
    null

  del = async (request: IValidationDeleteRequest) =>
    this.restDelete(`${BASE_PATH}/${request.uid}`)

  create = async (request: IValidationCreateRequest) =>
    this.restPost<IValidation>(BASE_PATH, request.validation)

  put = async (request: IValidationPutRequest) =>
    this.restPut<IValidation>(
      `${BASE_PATH}/${request.validation.uid}`,
      request.validation,
    )

  public async validateData(
    request: IValidateDataRequest,
  ): Promise<IValidateDataResponse> {
    // restClient unwraps the `{ result }` envelope (see FetchApiClient.fetchJson).
    // The REST endpoint returns `validated` as a string enum name; decode it to
    // the numeric UI.API.Validated enum that consumers compare against.
    const response = await this.restPost<
      Omit<IValidateDataResponse, 'validated'> & { validated: string }
    >(`${BASE_PATH}/validate`, request)
    return {
      ...response,
      validated: UI.API.validatedFromJSON(response.validated),
    }
  }

  public getItem(validation: IValidation) {
    const { uid, name, tenantId, jsonSchema } = validation
    if (!uid || !name || !jsonSchema) throw Error('Invalid response')
    return { ...validation, uid, name, tenantId, jsonSchema }
  }
}
